import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Paths,
  CalibrationSettings,
  CalibrationSettingsResponse,
  CalibrationHistoryResponse,
  CalibrationSuggestion,
  WeightOptimizationResult,
} from './types';

import { ProviderProps } from 'candidate_profile_redesign/store/use_candidate_profile';
import {
  standardRequest,
  standardRequestWithErrorFlash,
} from 'shared/utils/request';
import { useRankingInProgress } from './contexts/ranking_in_progress_context';
import URI from 'urijs';
import { withLocalePrefix } from 'shared/utils/translation';
import type { TalentMatchingSource } from 'talent_matching/lib/tracker';

const t = withLocalePrefix('talent_matching');

export const useTopSkills = (
  paths: Paths,
  enabled = true,
  skillsCount = 5,
  regenerate = false
) =>
  useQuery({
    queryKey: ['topSkills', paths.topSkillsPath],
    queryFn: async () => {
      const { data } = await standardRequest.get(paths.topSkillsPath, {
        params: { skills_count: skillsCount, regenerate },
      });
      return data;
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useIndustries = (paths: Paths, enabled = true) =>
  useQuery({
    queryKey: ['industries', paths.industriesPath],
    queryFn: async () => {
      const { data } = await standardRequest.get(paths.industriesPath);
      return data;
    },
    enabled,
  });

export const useCalibrationSettings = (paths: Paths, enabled = true) => {
  const query = useQuery<CalibrationSettingsResponse>({
    queryKey: ['calibrationSettings', paths.calibrationSettingsPath],
    queryFn: async () => {
      const { data } = await standardRequest.get(paths.calibrationSettingsPath);
      return data as CalibrationSettingsResponse;
    },
    enabled,
  });

  return query;
};

export const useCalibrationSuggestions = (paths: Paths, enabled = false) =>
  useQuery<{ suggestions: CalibrationSuggestion[] }>({
    queryKey: ['calibrationSuggestions', paths.calibrationSuggestionsPath],
    queryFn: async () => {
      const { data } = await standardRequest.get(
        paths.calibrationSuggestionsPath
      );
      const result = (data as { suggestions: WeightOptimizationResult })
        .suggestions;
      if (!result?.success) {
        return { suggestions: [] };
      }
      const suggestions: CalibrationSuggestion[] = result.skills
        .map((name, i) => ({
          name,
          current_importance: result.initial_weights[i],
          suggested_importance: result.optimized_weights[i],
        }))
        .filter((s) => s.current_importance !== s.suggested_importance);
      return { suggestions };
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useCalibrationHistory = (paths: Paths, enabled = true) =>
  useQuery<CalibrationHistoryResponse>({
    queryKey: ['calibrationHistory', paths.calibrationHistoryPath],
    queryFn: async () => {
      const { data } = await standardRequest.get(paths.calibrationHistoryPath);
      return data as CalibrationHistoryResponse;
    },
    enabled,
    refetchOnWindowFocus: false,
  });

type CreateCalibrationSettingsRequest = {
  settings: CalibrationSettings;
  errorMessage?: string;
};

export const useCreateCalibrationSettings = (paths: Paths) => {
  const { enablePolling } = useRankingInProgress();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      settings,
      errorMessage,
    }: CreateCalibrationSettingsRequest) => {
      const { data } = await standardRequestWithErrorFlash.post(
        paths.calibrationSettingsPath,
        settings,
        {
          errorMessage,
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['calibrationSettings', paths.calibrationSettingsPath],
      });
      queryClient.invalidateQueries({
        queryKey: ['calibrationHistory', paths.calibrationHistoryPath],
      });
      queryClient.invalidateQueries({
        queryKey: ['my-greenhouse-candidates'],
      });
      enablePolling();
    },
  });
};

export const useApplicationHistory = (
  url: string,
  searchParams?: Record<string, unknown>
) => {
  const query = useQuery({
    queryKey: ['applicationHistory', url, searchParams],
    queryFn: async () => {
      const { data } = await standardRequest.get(url, {
        params: searchParams,
      });
      return data;
    },
    enabled: !!url,
  });

  return query;
};

export const useGenerateSkillsDescription = (paths: Paths, enabled = true) =>
  useQuery<string>({
    queryKey: [
      'generateSkillsDescription',
      paths.generateSkillsDescriptionPath,
    ],
    queryFn: async () => {
      const { data } = await standardRequest.get(
        paths.generateSkillsDescriptionPath
      );
      return data.description;
    },
    enabled,
  });

export const useCandidateProfileProps = (providerPropsPath: string) =>
  useQuery<ProviderProps>({
    queryKey: ['candidateProfileProviderProps', providerPropsPath],
    queryFn: async () => {
      const { data } = await standardRequest.get(providerPropsPath);
      return data;
    },
  });

export const useGenerateCalibrationFromText = (paths: Paths) =>
  useMutation({
    mutationFn: async (params: {
      user_prompt: string;
      include_job_description?: boolean;
      include_scorecard_attributes?: boolean;
    }) => {
      const { data } = await standardRequest.post(
        paths.generateCalibrationFromTextPath,
        {
          user_prompt: params.user_prompt,
          include_job_description: params.include_job_description,
          include_scorecard_attributes: params.include_scorecard_attributes,
        }
      );
      return data;
    },
  });

export const fetchSavedSearch = async (
  path: string,
  id: number,
  featureType = 'talent_matching'
) => {
  const url = new URI(`${path}/${id}`)
    .addQuery({ feature_type: featureType })
    .toString();

  const { data } = await standardRequest.get(url);

  return data.saved_search.value;
};

export const createSavedSearch = async (
  path: string,
  search_params: string,
  name: string,
  featureType: string
) => {
  const url = new URI(path).addQuery({ feature_type: featureType }).toString();

  const { data } = await standardRequestWithErrorFlash.post(
    url,
    {
      search_params,
      name,
    },
    {
      successMessage: t('filter_row.saved_filters.success.save'),
    }
  );

  return data.saved_search;
};

type RediscoveryActionType =
  | 'added_as_prospect'
  | 'tags_amended'
  | 'sent_email'
  | 'added_to_campaign'
  | 'added_to_this_job'
  | 'added_to_another_job';

export const createRediscoveryAction = async (params: {
  action_type: RediscoveryActionType;
  application_id: number;
  hiring_plan_id: number;
  metadata?: Record<string, unknown>;
}): Promise<{ id: number } | null> => {
  try {
    const { data } = await standardRequest.post(
      '/talent_matching/rediscovery_actions',
      params
    );
    return data;
  } catch (error) {
    // ignore errors since it's not critical
    return null;
  }
};

export const createBulkRediscoveryActions = async (params: {
  action_type: RediscoveryActionType;
  application_ids: number[];
  hiring_plan_id: number;
  metadata?: Record<string, unknown>;
}): Promise<void> => {
  try {
    await standardRequest.post(
      '/talent_matching/rediscovery_actions/bulk_create',
      params
    );
  } catch {
    // ignore errors since it's not critical
  }
};

// Analytics for a bulk action. The backend fans out one event per application
// sharing `bulk_action_uuid` + `bulk_action_size`. Resolves when the request
// settles and never rejects, so callers can optionally await it before a
// navigation (e.g. a page reload) without risking the action itself.
export const trackBulkUserAction = (params: {
  source: TalentMatchingSource;
  tracked_action: string;
  application_ids: number[];
  hiring_plan_id: number;
  bulk_action_uuid: string;
  app_review_session_id?: number | null;
  // Per-application fields promoted to top-level on each emitted event so the
  // bulk shape matches the single (client-side) path. The backend allowlists
  // which keys are promoted; everything else belongs in `metadata`.
  entity_fields?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}): Promise<void> =>
  standardRequest
    .post('/talent_matching/tracking/bulk', params)
    .then(() => undefined)
    .catch(() => undefined);

export const useBookmarkUsers = (path: string) =>
  useQuery<{ id: number; name: string }[]>({
    queryKey: ['bookmarkUsers', path],
    queryFn: async () => {
      const { data } = await standardRequest.get(path);
      return data;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

export const updateSavedSearch = async (
  path: string,
  id: number,
  search_params: string,
  name: string,
  featureType: string
) => {
  const url = new URI(`${path}/${id}`)
    .addQuery({ feature_type: featureType })
    .toString();

  const { data } = await standardRequestWithErrorFlash.put(
    url,
    {
      search_params,
      name,
    },
    {
      successMessage: t('filter_row.saved_filters.success.update'),
    }
  );

  return data.saved_search;
};

export const setDefaultSavedSearch = async (
  path: string,
  args: {
    hiringPlanId: number;
    featureType: string;
    savedSearchId?: number | null;
    greenhouseFilterKey?: string | null;
  }
): Promise<void> => {
  try {
    await standardRequest.put(path, {
      hiring_plan_id: args.hiringPlanId,
      feature_type: args.featureType,
      saved_search_id: args.savedSearchId ?? null,
      greenhouse_filter_key: args.greenhouseFilterKey ?? null,
    });
  } catch (error) {
    // Quiet preference write; UI state is already correct. Next session
    // simply won't auto-load if this fails.
    console.warn('Failed to persist default saved search', error);
  }
};
